/**
 * STORY-298: "Save as PNG" for an interactive chart.
 *
 * The chart is an SVG on screen. Exporting it: clone the SVG, make it stand
 * alone (white background, inline colours instead of theme classes and
 * `currentColor`, a system font stack, the legend of the visible series drawn
 * into it, the hover layer removed), load that as an image from a blob URL,
 * draw it to a canvas and save the canvas as PNG. No foreignObject and no
 * external reference, so the canvas is not tainted. If the browser still
 * refuses (toBlob throws or yields nothing), the same standalone SVG is
 * downloaded instead and the caller says so.
 *
 * The file name is the time of the export only — never a host or tenant name.
 */
import { format } from 'date-fns';

export const EXPORT_INK = '#1f2937';
export const EXPORT_FONT =
  "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

export interface LegendEntry {
  label: string;
  color: string;
  dash?: string;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/** chart-<yyyyMMdd-HHmm>.<ext> */
export function chartFilename(when: Date = new Date(), ext: 'png' | 'svg' = 'png'): string {
  return `chart-${format(when, 'yyyyMMdd-HHmm')}.${ext}`;
}

/** The ImportExportSettings download pattern, for any blob. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const LEGEND_ROW = 20;
const LEGEND_COL = 210;

/**
 * The on-screen SVG as a standalone document: what the user sees, plus the
 * legend, on white. Elements marked `data-export-skip` (tooltip, crosshair,
 * focus ring, drag selection) are dropped; `data-export-color` replaces a
 * mark's theme-dependent colour with the light palette's.
 */
export function standaloneChartSvg(
  svg: SVGSVGElement,
  legend: LegendEntry[],
): { markup: string; width: number; height: number } {
  const vb = svg.viewBox?.baseVal;
  const width = vb && vb.width ? vb.width : Number(svg.getAttribute('width')) || 720;
  const baseHeight = vb && vb.height ? vb.height : Number(svg.getAttribute('height')) || 360;
  const perRow = Math.max(1, Math.floor((width - 32) / LEGEND_COL));
  const legendRows = legend.length ? Math.ceil(legend.length / perRow) : 0;
  const height = baseHeight + (legendRows ? legendRows * LEGEND_ROW + 12 : 0);

  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.querySelectorAll('[data-export-skip]').forEach((el) => el.remove());
  clone.querySelectorAll('[data-export-color]').forEach((el) => {
    const color = el.getAttribute('data-export-color') as string;
    if (el.hasAttribute('stroke') && el.getAttribute('stroke') !== 'none') el.setAttribute('stroke', color);
    if (el.hasAttribute('fill') && el.getAttribute('fill') !== 'none') el.setAttribute('fill', color);
  });
  clone.querySelectorAll('[class]').forEach((el) => el.removeAttribute('class'));
  clone.removeAttribute('class');
  clone.setAttribute('xmlns', SVG_NS);
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));
  clone.setAttribute('viewBox', `0 0 ${width} ${height}`);
  clone.setAttribute('color', EXPORT_INK);
  clone.setAttribute('font-family', EXPORT_FONT);

  const doc = clone.ownerDocument;
  const bg = doc.createElementNS(SVG_NS, 'rect');
  bg.setAttribute('x', '0');
  bg.setAttribute('y', '0');
  bg.setAttribute('width', String(width));
  bg.setAttribute('height', String(height));
  bg.setAttribute('fill', '#ffffff');
  clone.insertBefore(bg, clone.firstChild);

  if (legend.length) {
    const g = doc.createElementNS(SVG_NS, 'g');
    g.setAttribute('data-legend', '');
    legend.forEach((entry, i) => {
      const x = 16 + (i % perRow) * LEGEND_COL;
      const y = baseHeight + 10 + Math.floor(i / perRow) * LEGEND_ROW;
      const line = doc.createElementNS(SVG_NS, 'line');
      line.setAttribute('x1', String(x));
      line.setAttribute('x2', String(x + 18));
      line.setAttribute('y1', String(y + 6));
      line.setAttribute('y2', String(y + 6));
      line.setAttribute('stroke', entry.color);
      line.setAttribute('stroke-width', '3');
      if (entry.dash) line.setAttribute('stroke-dasharray', entry.dash);
      const text = doc.createElementNS(SVG_NS, 'text');
      text.setAttribute('x', String(x + 24));
      text.setAttribute('y', String(y + 10));
      text.setAttribute('font-size', '11');
      text.setAttribute('fill', EXPORT_INK);
      const label = entry.label.length > 30 ? `${entry.label.slice(0, 29)}…` : entry.label;
      text.textContent = label;
      g.appendChild(line);
      g.appendChild(text);
    });
    clone.appendChild(g);
  }
  return { markup: new XMLSerializer().serializeToString(clone), width, height };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('chart image failed to load'));
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((b) => resolve(b), 'image/png');
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Render the standalone SVG to a PNG and download it. Resolves 'png', or
 * 'svg' when the browser refused the canvas and the SVG was downloaded
 * instead.
 */
export async function exportChartPng(
  standalone: { markup: string; width: number; height: number },
  when: Date = new Date(),
): Promise<'png' | 'svg'> {
  const svgBlob = new Blob([standalone.markup], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);
  try {
    const img = await loadImage(url);
    const scale = 2; // crisp in a report
    const canvas = document.createElement('canvas');
    canvas.width = standalone.width * scale;
    canvas.height = standalone.height * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const png = await canvasToBlob(canvas);
    if (!png) throw new Error('empty png');
    downloadBlob(png, chartFilename(when, 'png'));
    return 'png';
  } catch {
    downloadBlob(svgBlob, chartFilename(when, 'svg'));
    return 'svg';
  } finally {
    URL.revokeObjectURL(url);
  }
}
